import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import ReactPaginate from 'react-paginate';
import { fetchPublicacionAuditoriaPrecioStockApi } from '../../../../api/axios';
import './AuditoriaPrecioStock.css';

const buildEmptyResponse = () => ({
    resumen: {
        precioListaActual: null,
        precioPromocionalActual: null,
        stockActual: null,
        ventas90Dias: 0,
        cantidadAlertas: 0
    },
    periodos: []
});

const toArray = (value, alternate) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(alternate)) return alternate;
    return [];
};

const firstArray = (...values) => {
    let fallbackEmptyArray = null;

    for (const value of values) {
        if (Array.isArray(value)) {
            if (value.length > 0) return value;
            fallbackEmptyArray = fallbackEmptyArray ?? value;
            continue;
        }
        if (Array.isArray(value?.content)) {
            if (value.content.length > 0) return value.content;
            fallbackEmptyArray = fallbackEmptyArray ?? value.content;
            continue;
        }
        if (Array.isArray(value?.Content)) {
            if (value.Content.length > 0) return value.Content;
            fallbackEmptyArray = fallbackEmptyArray ?? value.Content;
            continue;
        }
        if (typeof value === 'string' && value.trim()) {
            const trimmed = value.trim();
            if (trimmed.startsWith('[')) {
                try {
                    const parsed = JSON.parse(trimmed);
                    if (Array.isArray(parsed)) {
                        if (parsed.length > 0) return parsed;
                        fallbackEmptyArray = fallbackEmptyArray ?? parsed;
                        continue;
                    }
                } catch {
                    continue;
                }
            }
            const valuesFromString = trimmed.split(',').map((item) => item.trim()).filter(Boolean);
            if (valuesFromString.length > 0) return valuesFromString;
        }
    }
    return fallbackEmptyArray ?? [];
};

const normalizeOrden = (orden) => {
    if (orden == null || orden === '') return null;
    if (typeof orden === 'number' || typeof orden === 'string') return String(orden);

    const value = orden?.idOrden
        ?? orden?.IdOrden
        ?? orden?.idOrdenVenta
        ?? orden?.IdOrdenVenta
        ?? orden?.idVenta
        ?? orden?.IdVenta
        ?? orden?.ventaId
        ?? orden?.VentaId
        ?? orden?.idOrdenMarketplace
        ?? orden?.IdOrdenMarketplace
        ?? orden?.ordenId
        ?? orden?.OrdenId
        ?? orden?.numeroOrden
        ?? orden?.NumeroOrden
        ?? orden?.numeroVenta
        ?? orden?.NumeroVenta
        ?? orden?.id
        ?? orden?.Id
        ?? orden?.codigo
        ?? orden?.Codigo;

    return value == null || value === '' ? null : String(value);
};

const normalizeOrdenesPeriodo = (periodo) => firstArray(
    periodo?.idsOrdenes,
    periodo?.IdsOrdenes,
    periodo?.idOrdenes,
    periodo?.IdOrdenes,
    periodo?.idsOrdenesIncluidas,
    periodo?.IdsOrdenesIncluidas,
    periodo?.ordenesIncluidas,
    periodo?.OrdenesIncluidas,
    periodo?.ventasIncluidas,
    periodo?.VentasIncluidas,
    periodo?.ordenes,
    periodo?.Ordenes,
    periodo?.ventas,
    periodo?.Ventas,
    periodo?.idsVentas,
    periodo?.IdsVentas,
    periodo?.idVentas,
    periodo?.IdVentas
).map(normalizeOrden).filter(Boolean);

const toNumberOrNull = (value) => {
    if (value == null || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const toMoneyOrNull = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;

        const sanitized = trimmed
            .replace(/[^\d,.-]/g, '')
            .replace(/\s+/g, '');

        if (!sanitized) return null;

        const sign = sanitized.startsWith('-') ? '-' : '';
        const unsigned = sanitized.replace(/-/g, '');

        if (!unsigned) return null;

        const strategies = [
            [/^\d{1,3}(\.\d{3})+,\d{1,4}$/, (raw) => raw.replace(/\./g, '').replace(',', '.')],
            [/^\d{1,3}(,\d{3})+\.\d{1,4}$/, (raw) => raw.replace(/,/g, '')],
            [/^\d{1,3}(\.\d{3})+$/, (raw) => raw.replace(/\./g, '')],
            [/^\d{1,3}(,\d{3})+$/, (raw) => raw.replace(/,/g, '')],
            [/^\d+,\d{1,4}$/, (raw) => raw.replace(',', '.')],
            [/^\d+\.\d{1,4}$/, (raw) => raw],
            [/^\d+$/, (raw) => raw]
        ];

        let normalized = null;

        for (const [pattern, transform] of strategies) {
            if (pattern.test(unsigned)) {
                normalized = transform(unsigned);
                break;
            }
        }

        if (normalized == null) {
            const lastComma = unsigned.lastIndexOf(',');
            const lastDot = unsigned.lastIndexOf('.');
            const lastSeparatorIndex = Math.max(lastComma, lastDot);

            if (lastSeparatorIndex >= 0) {
                const integerPart = unsigned.slice(0, lastSeparatorIndex).replace(/[.,]/g, '');
                const decimalPart = unsigned.slice(lastSeparatorIndex + 1).replace(/[.,]/g, '');
                normalized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
            } else {
                normalized = unsigned.replace(/[.,]/g, '');
            }
        }

        const parsed = Number(`${sign}${normalized}`);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const computePercentDelta = (before, after) => {
    if (!Number.isFinite(before) || !Number.isFinite(after) || before === 0) return null;
    return ((after - before) / Math.abs(before)) * 100;
};

const computeNumericDelta = (before, after) => {
    if (!Number.isFinite(before) || !Number.isFinite(after)) return null;
    return after - before;
};

const maybeFixShiftedPromotionalValue = (value, listPrice, referenceValue) => {
    if (
        !Number.isFinite(value) ||
        value <= 0 ||
        value < 100000 ||
        !Number.isInteger(value) ||
        value % 100 !== 0
    ) {
        return value;
    }

    const candidate = value / 100;
    const hasListPrice = Number.isFinite(listPrice) && listPrice > 0;
    const hasReferenceValue = Number.isFinite(referenceValue) && referenceValue > 0;

    const rawRatioToList = hasListPrice ? (value / listPrice) : null;
    const candidateRatioToList = hasListPrice ? (candidate / listPrice) : null;
    const rawDistanceToReference = hasReferenceValue ? Math.abs(value - referenceValue) : null;
    const candidateDistanceToReference = hasReferenceValue ? Math.abs(candidate - referenceValue) : null;

    const looksShiftedAgainstList = hasListPrice
        && rawRatioToList > 20
        && candidateRatioToList <= 5;

    const candidateIsMuchCloserToReference = hasReferenceValue
        && candidateDistanceToReference * 10 <= rawDistanceToReference;

    return (looksShiftedAgainstList || candidateIsMuchCloserToReference) ? candidate : value;
};

const normalizeAlert = (alerta) => {
    if (!alerta) return null;
    if (typeof alerta === 'string') {
        return {
            codigo: alerta,
            descripcion: alerta.replace(/_/g, ' ')
        };
    }

    return {
        codigo: alerta?.codigo ?? alerta?.Codigo ?? alerta?.id ?? alerta?.Id ?? 'alerta',
        descripcion: alerta?.descripcion ?? alerta?.Descripcion ?? alerta?.nombre ?? alerta?.Nombre ?? 'Alerta'
    };
};

const normalizeEvento = (evento, index, periodContext = {}) => {
    const antes = evento?.antes ?? evento?.Antes ?? {};
    const despues = evento?.despues ?? evento?.Despues ?? {};
    const delta = evento?.delta ?? evento?.Delta ?? {};

    const antesPrecioLista = toMoneyOrNull(antes?.precioLista ?? antes?.PrecioLista);
    const despuesPrecioLista = toMoneyOrNull(despues?.precioLista ?? despues?.PrecioLista);
    const despuesPrecioPromocionalInicial = toMoneyOrNull(despues?.precioPromocional ?? despues?.PrecioPromocional);
    const antesPrecioPromocionalInicial = toMoneyOrNull(antes?.precioPromocional ?? antes?.PrecioPromocional);
    const despuesPrecioPromocional = maybeFixShiftedPromotionalValue(
        despuesPrecioPromocionalInicial,
        despuesPrecioLista,
        periodContext?.precioPromocional ?? antesPrecioPromocionalInicial
    );
    const antesPrecioPromocional = maybeFixShiftedPromotionalValue(
        antesPrecioPromocionalInicial,
        antesPrecioLista,
        despuesPrecioPromocional ?? periodContext?.precioPromocional
    );
    const antesStock = toNumberOrNull(antes?.stock ?? antes?.Stock);
    const despuesStock = toNumberOrNull(despues?.stock ?? despues?.Stock);

    return {
        id: evento?.id ?? evento?.Id ?? `${evento?.fecha ?? evento?.Fecha ?? 'evento'}-${index}`,
        fecha: evento?.fecha ?? evento?.Fecha ?? null,
        origen: evento?.origen ?? evento?.Origen ?? '-',
        usuario: evento?.usuario ?? evento?.Usuario ?? '-',
        idUsuario: evento?.idUsuario ?? evento?.IdUsuario ?? null,
        antes: {
            precioLista: antesPrecioLista,
            precioPromocional: antesPrecioPromocional,
            stock: antesStock
        },
        despues: {
            precioLista: despuesPrecioLista,
            precioPromocional: despuesPrecioPromocional,
            stock: despuesStock
        },
        delta: {
            precioListaAbs: computeNumericDelta(antesPrecioLista, despuesPrecioLista)
                ?? toMoneyOrNull(delta?.precioListaAbs ?? delta?.PrecioListaAbs ?? delta?.precioLista ?? delta?.PrecioLista),
            precioListaPct: computePercentDelta(antesPrecioLista, despuesPrecioLista)
                ?? toNumberOrNull(delta?.precioListaPct ?? delta?.PrecioListaPct),
            precioPromocionalAbs: computeNumericDelta(antesPrecioPromocional, despuesPrecioPromocional)
                ?? toMoneyOrNull(delta?.precioPromocionalAbs ?? delta?.PrecioPromocionalAbs ?? delta?.precioPromocional ?? delta?.PrecioPromocional),
            precioPromocionalPct: computePercentDelta(antesPrecioPromocional, despuesPrecioPromocional)
                ?? toNumberOrNull(delta?.precioPromocionalPct ?? delta?.PrecioPromocionalPct),
            stockAbs: computeNumericDelta(antesStock, despuesStock)
                ?? toNumberOrNull(delta?.stockAbs ?? delta?.StockAbs ?? delta?.stock ?? delta?.Stock),
            stockPct: computePercentDelta(antesStock, despuesStock)
                ?? toNumberOrNull(delta?.stockPct ?? delta?.StockPct)
        },
        alertas: toArray(evento?.alertas, evento?.Alertas).map(normalizeAlert).filter(Boolean)
    };
};

const normalizePeriodo = (periodo, index) => {
    const precioLista = toMoneyOrNull(periodo?.precioLista ?? periodo?.PrecioLista);
    const precioPromocionalInicial = toMoneyOrNull(periodo?.precioPromocional ?? periodo?.PrecioPromocional);
    const precioPromocional = maybeFixShiftedPromotionalValue(
        precioPromocionalInicial,
        precioLista,
        null
    );

    return {
        id: periodo?.id ?? periodo?.Id ?? `${periodo?.fechaDesde ?? periodo?.FechaDesde ?? 'periodo'}-${index}`,
        fechaDesde: periodo?.fechaDesde ?? periodo?.FechaDesde ?? null,
        fechaHasta: periodo?.fechaHasta ?? periodo?.FechaHasta ?? null,
        precioLista,
        precioPromocional,
        stockInicial: toNumberOrNull(periodo?.stockInicial ?? periodo?.StockInicial),
        stockFinal: toNumberOrNull(periodo?.stockFinal ?? periodo?.StockFinal),
        cantidadVentas: toNumberOrNull(periodo?.cantidadVentas ?? periodo?.CantidadVentas) ?? 0,
        idsOrdenes: normalizeOrdenesPeriodo(periodo),
        alertas: toArray(periodo?.alertas, periodo?.Alertas).map(normalizeAlert).filter(Boolean),
        eventos: toArray(periodo?.eventos, periodo?.Eventos).map((evento, eventIndex) => normalizeEvento(evento, eventIndex, {
            precioLista,
            precioPromocional
        }))
    };
};

const normalizeAuditoriaResponse = (rawData) => {
    const data = rawData?.data ?? rawData ?? {};
    const resumen = data?.resumen ?? data?.Resumen ?? {};
    const periodos = toArray(data?.periodos, data?.Periodos).map(normalizePeriodo);

    return {
        resumen: {
            precioListaActual: toMoneyOrNull(resumen?.precioListaActual ?? resumen?.PrecioListaActual),
            precioPromocionalActual: toMoneyOrNull(resumen?.precioPromocionalActual ?? resumen?.PrecioPromocionalActual),
            stockActual: toNumberOrNull(resumen?.stockActual ?? resumen?.StockActual),
            ventas90Dias: toNumberOrNull(resumen?.ventas90Dias ?? resumen?.Ventas90Dias) ?? 0,
            cantidadAlertas: toNumberOrNull(resumen?.cantidadAlertas ?? resumen?.CantidadAlertas) ?? 0
        },
        periodos
    };
};

const currencyFormatter = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 2
});

const integerFormatter = new Intl.NumberFormat('es-AR');

const formatMoney = (value) => {
    if (value == null) return '-';
    return currencyFormatter.format(value);
};

const formatSignedMoney = (value) => {
    if (value == null) return '-';
    const sign = value > 0 ? '+' : '';
    return `${sign}${currencyFormatter.format(value)}`;
};

const formatInteger = (value) => {
    if (value == null) return '-';
    return integerFormatter.format(value);
};

const formatSignedInteger = (value) => {
    if (value == null) return '-';
    const sign = value > 0 ? '+' : '';
    return `${sign}${integerFormatter.format(value)}`;
};

const formatPercent = (value) => {
    if (value == null) return '-';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
};

const formatVariation = (numericValue, percentValue, formatter) => {
    if (numericValue == null && percentValue == null) return '-';
    return `${formatter(numericValue)} (${formatPercent(percentValue)})`;
};

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const renderOrdenesPeriodo = (periodo) => {
    if (periodo.idsOrdenes?.length) {
        return (
            <div className="auditoria-publicacion__orders">
                {periodo.idsOrdenes.map((idOrden) => (
                    <span key={`${periodo.id}-${idOrden}`} className="auditoria-publicacion__order-badge">
                        {idOrden}
                    </span>
                ))}
            </div>
        );
    }

    return periodo.cantidadVentas > 0
        ? <span className="auditoria-publicacion__muted">No informado por API</span>
        : <span className="auditoria-publicacion__muted">Sin ordenes</span>;
};

const monthsOptions = [
    { label: '30 dias', value: 1 },
    { label: '60 dias', value: 2 },
    { label: '90 dias', value: 3 }
];

const pageSizeOptions = [5, 10, 20];

function AuditoriaPrecioStock({ idPublicacion, publicacion }) {
    const [meses, setMeses] = useState(1);
    const [soloAlertas, setSoloAlertas] = useState(false);
    const [soloCambiosReales, setSoloCambiosReales] = useState(true);
    const [expandedPeriodId, setExpandedPeriodId] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [response, setResponse] = useState(buildEmptyResponse());

    useEffect(() => {
        let active = true;

        const fetchData = async () => {
            if (!idPublicacion) {
                setResponse(buildEmptyResponse());
                return;
            }

            setLoading(true);
            setError('');

            try {
                const data = await fetchPublicacionAuditoriaPrecioStockApi({
                    idPublicacion,
                    meses,
                    soloAlertas,
                    soloCambiosReales,
                    incluirOrdenes: true,
                    incluirVentas: true,
                    incluirIdsOrdenes: true,
                    incluirDetalleOrdenes: true
                });

                if (!active) return;
                setResponse(normalizeAuditoriaResponse(data));
            } catch (err) {
                if (!active) return;

                const status = err?.response?.status;
                if (status === 404) {
                    setError('La auditoria todavia no esta disponible para esta publicacion.');
                } else {
                    setError('No se pudo cargar la auditoria de precio y stock.');
                }
                setResponse(buildEmptyResponse());
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            active = false;
        };
    }, [idPublicacion, meses, soloAlertas, soloCambiosReales]);

    const resumen = useMemo(() => ({
        precioListaActual: response?.resumen?.precioListaActual ?? toMoneyOrNull(publicacion?.precioLista),
        precioPromocionalActual: response?.resumen?.precioPromocionalActual ?? toMoneyOrNull(publicacion?.precioPromocional),
        stockActual: response?.resumen?.stockActual ?? toNumberOrNull(publicacion?.stock),
        ventas90Dias: response?.resumen?.ventas90Dias ?? 0,
        cantidadAlertas: response?.resumen?.cantidadAlertas ?? 0
    }), [response, publicacion]);

    const periodos = response?.periodos ?? [];
    const pageCount = Math.ceil(periodos.length / pageSize);
    const safeCurrentPage = pageCount > 0 ? Math.min(currentPage, pageCount - 1) : 0;
    const periodosPaginados = useMemo(() => {
        const startIndex = safeCurrentPage * pageSize;
        return periodos.slice(startIndex, startIndex + pageSize);
    }, [periodos, pageSize, safeCurrentPage]);
    const firstVisibleResult = periodos.length > 0 ? (safeCurrentPage * pageSize) + 1 : 0;
    const lastVisibleResult = Math.min((safeCurrentPage + 1) * pageSize, periodos.length);

    useEffect(() => {
        setCurrentPage(0);
        setExpandedPeriodId(null);
    }, [idPublicacion, meses, soloAlertas, soloCambiosReales, pageSize]);

    useEffect(() => {
        if (currentPage !== safeCurrentPage) {
            setCurrentPage(safeCurrentPage);
            setExpandedPeriodId(null);
        }
    }, [currentPage, safeCurrentPage]);

    const handlePageChange = ({ selected }) => {
        setCurrentPage(selected);
        setExpandedPeriodId(null);
    };

    return (
        <div className="auditoria-publicacion">
            <div className="auditoria-publicacion__summary">
                <div className="auditoria-publicacion__card">
                    <span className="auditoria-publicacion__label">Precio lista actual</span>
                    <strong>{formatMoney(resumen.precioListaActual)}</strong>
                </div>
                <div className="auditoria-publicacion__card">
                    <span className="auditoria-publicacion__label">Precio promo actual</span>
                    <strong>{formatMoney(resumen.precioPromocionalActual)}</strong>
                </div>
                <div className="auditoria-publicacion__card">
                    <span className="auditoria-publicacion__label">Stock actual</span>
                    <strong>{formatInteger(resumen.stockActual)}</strong>
                </div>
                <div className="auditoria-publicacion__card">
                    <span className="auditoria-publicacion__label">Ventas del periodo</span>
                    <strong>{formatInteger(resumen.ventas90Dias)}</strong>
                </div>
                <div className="auditoria-publicacion__card auditoria-publicacion__card--alert">
                    <span className="auditoria-publicacion__label">Alertas activas</span>
                    <strong>{formatInteger(resumen.cantidadAlertas)}</strong>
                </div>
            </div>

            <div className="auditoria-publicacion__toolbar">
                <div className="auditoria-publicacion__months">
                    {monthsOptions.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            className={`auditoria-publicacion__filter-btn ${meses === option.value ? 'is-active' : ''}`}
                            onClick={() => setMeses(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                <label className="auditoria-publicacion__checkbox">
                    <input
                        type="checkbox"
                        checked={soloCambiosReales}
                        onChange={(event) => setSoloCambiosReales(event.target.checked)}
                    />
                    <span>Ocultar actualizaciones sin cambio real</span>
                </label>
                <label className="auditoria-publicacion__checkbox">
                    <input
                        type="checkbox"
                        checked={soloAlertas}
                        onChange={(event) => setSoloAlertas(event.target.checked)}
                    />
                    <span>Solo alertas</span>
                </label>
            </div>

            <div className="auditoria-publicacion__hint">
                Se muestran hasta 3 meses de cambios y se omiten eventos sin variacion real de precio o stock.
            </div>

            {loading && (
                <div className="auditoria-publicacion__state">
                    Cargando auditoria...
                </div>
            )}

            {!loading && error && (
                <div className="auditoria-publicacion__state auditoria-publicacion__state--error">
                    {error}
                </div>
            )}

            {!loading && !error && periodos.length === 0 && (
                <div className="auditoria-publicacion__state">
                    No hay cambios auditables para el periodo seleccionado.
                </div>
            )}

            {!loading && !error && periodos.length > 0 && (
                <>
                    <div className="auditoria-publicacion__pagination-summary">
                        <span>
                            Mostrando {firstVisibleResult}-{lastVisibleResult} de {periodos.length} resultados
                        </span>
                        <label className="auditoria-publicacion__page-size">
                            <span>Resultados por pagina</span>
                            <select
                                value={pageSize}
                                onChange={(event) => setPageSize(Number(event.target.value))}
                            >
                                {pageSizeOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <div className="auditoria-publicacion__table-wrap">
                        <table className="table auditoria-publicacion__table">
                        <thead>
                            <tr>
                                <th>Periodo</th>
                                <th>Precio lista</th>
                                <th>Precio promo</th>
                                <th>Stock</th>
                                <th>Ventas</th>
                                <th>Ordenes</th>
                                <th>Alertas</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {periodosPaginados.map((periodo) => {
                                const expanded = expandedPeriodId === periodo.id;
                                return (
                                    <React.Fragment key={periodo.id}>
                                        <tr>
                                            <td>
                                                <div className="auditoria-publicacion__period">
                                                    <strong>{formatDate(periodo.fechaDesde)}</strong>
                                                    <span>hasta {formatDate(periodo.fechaHasta)}</span>
                                                </div>
                                            </td>
                                            <td>{formatMoney(periodo.precioLista)}</td>
                                            <td>{formatMoney(periodo.precioPromocional)}</td>
                                            <td>
                                                <div className="auditoria-publicacion__stock-cell">
                                                    <span>Inicio: {formatInteger(periodo.stockInicial)}</span>
                                                    <span>Cierre: {formatInteger(periodo.stockFinal)}</span>
                                                </div>
                                            </td>
                                            <td>{formatInteger(periodo.cantidadVentas)}</td>
                                            <td>{renderOrdenesPeriodo(periodo)}</td>
                                            <td>
                                                <div className="auditoria-publicacion__alerts">
                                                    {periodo.alertas?.length
                                                        ? periodo.alertas.map((alerta) => (
                                                            <span key={`${periodo.id}-${alerta.codigo}`} className="auditoria-publicacion__badge">
                                                                {alerta.descripcion}
                                                            </span>
                                                        ))
                                                        : <span className="auditoria-publicacion__muted">Sin alertas</span>}
                                                </div>
                                            </td>
                                            <td className="text-right">
                                                <button
                                                    type="button"
                                                    className="auditoria-publicacion__toggle"
                                                    onClick={() => setExpandedPeriodId(expanded ? null : periodo.id)}
                                                >
                                                    {expanded ? 'Ocultar detalle' : 'Ver detalle'}
                                                </button>
                                            </td>
                                        </tr>
                                        {expanded && (
                                            <tr className="auditoria-publicacion__detail-row">
                                                <td colSpan="8">
                                                    {periodo.eventos?.length ? (
                                                        <table className="table auditoria-publicacion__events-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Fecha</th>
                                                                    <th>Origen</th>
                                                                    <th>Usuario</th>
                                                                    <th>Antes</th>
                                                                    <th>Despues</th>
                                                                    <th>Variaci&oacute;n</th>
                                                                    <th>Alertas</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {periodo.eventos.map((evento) => (
                                                                    <tr key={evento.id}>
                                                                        <td>{formatDate(evento.fecha)}</td>
                                                                        <td>{evento.origen}</td>
                                                                        <td>{evento.usuario}{evento.idUsuario ? ` (#${evento.idUsuario})` : ''}</td>
                                                                        <td>
                                                                            <div>Lista: {formatMoney(evento.antes.precioLista)}</div>
                                                                            <div>Promo: {formatMoney(evento.antes.precioPromocional)}</div>
                                                                            <div>Stock: {formatInteger(evento.antes.stock)}</div>
                                                                        </td>
                                                                        <td>
                                                                            <div>Lista: {formatMoney(evento.despues.precioLista)}</div>
                                                                            <div>Promo: {formatMoney(evento.despues.precioPromocional)}</div>
                                                                            <div>Stock: {formatInteger(evento.despues.stock)}</div>
                                                                        </td>
                                                                        <td>
                                                                            <div className="auditoria-publicacion__variation-line">
                                                                                <span>Lista:</span>
                                                                                <strong>{formatVariation(evento.delta.precioListaAbs, evento.delta.precioListaPct, formatSignedMoney)}</strong>
                                                                            </div>
                                                                            <div className="auditoria-publicacion__variation-line">
                                                                                <span>Promo:</span>
                                                                                <strong>{formatVariation(evento.delta.precioPromocionalAbs, evento.delta.precioPromocionalPct, formatSignedMoney)}</strong>
                                                                            </div>
                                                                            <div className="auditoria-publicacion__variation-line">
                                                                                <span>Stock:</span>
                                                                                <strong>{formatVariation(evento.delta.stockAbs, evento.delta.stockPct, formatSignedInteger)}</strong>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <div className="auditoria-publicacion__alerts">
                                                                                {evento.alertas?.length
                                                                                    ? evento.alertas.map((alerta) => (
                                                                                        <span key={`${evento.id}-${alerta.codigo}`} className="auditoria-publicacion__badge">
                                                                                            {alerta.descripcion}
                                                                                        </span>
                                                                                    ))
                                                                                    : <span className="auditoria-publicacion__muted">-</span>}
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    ) : (
                                                        <div className="auditoria-publicacion__muted">
                                                            Este periodo no trajo eventos detallados.
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                    {pageCount > 1 && (
                        <ReactPaginate
                            previousLabel={"Anterior"}
                            nextLabel={"Siguiente"}
                            breakLabel={"..."}
                            pageCount={pageCount}
                            marginPagesDisplayed={1}
                            pageRangeDisplayed={3}
                            onPageChange={handlePageChange}
                            containerClassName={"pagination auditoria-publicacion__pagination"}
                            activeClassName={"active"}
                            forcePage={safeCurrentPage}
                        />
                    )}
                </>
            )}
        </div>
    );
}

const mapStateToProps = (state) => ({
    publicacion: state?.publicaciones?.publicacion ?? {}
});

export default connect(mapStateToProps)(AuditoriaPrecioStock);
